<template>
  <div class="tags-picker">
    <!-- Champ de saisie principal -->
    <div class="relative">
      <div class="flex flex-wrap gap-2 p-2 rounded-md border border-gray-300 focus-within:border-blue-500 focus-within:ring-blue-500"
           :class="{ 'bg-gray-50 border-gray-300': readonly }">
        <!-- Tags sélectionnés -->
        <div v-for="tag in selectedTags" :key="tag" class="flex items-center bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
          <span class="text-sm">{{ tag }}</span>
          <button v-if="!readonly && !disabled" class="ml-1 text-blue-600 hover:text-blue-800" @click="removeTag(tag)">
            <XIcon class="h-4 w-4" />
          </button>
        </div>

        <!-- Input -->
        <input
          v-if="!readonly"
          id="tags-input"
          v-model="inputValue"
          name="tags-input"
          class="flex-grow min-w-[100px] outline-none border-none focus:ring-0 p-1"
          :placeholder="placeholder || 'Ajouter un rôle...'"
          :disabled="disabled"
          @input="handleInput"
          @keydown.enter.prevent="handleEnter"
          @keydown.backspace="handleBackspace"
          @keydown.down="handleArrowDown"
          @keydown.up="handleArrowUp"
          @focus="showSuggestions = true"
        />
        
        <!-- Placeholder en mode readonly si aucun tag -->
        <span v-if="readonly && selectedTags.length === 0" class="text-gray-500 text-sm p-1">
          {{ placeholder || 'Aucun rôle défini' }}
        </span>
      </div>

      <!-- Suggestions -->
      <div
        v-if="!readonly && showSuggestions && (suggestions.length > 0 || inputValue)"
        class="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg">
        <div v-if="suggestions.length > 0">
          <div
            v-for="(suggestion, index) in suggestions"
            :key="suggestion"
            :class="['text-sm p-2 cursor-pointer hover:bg-gray-100',
                     {'bg-gray-100': index === selectedIndex}]"
            @click="selectSuggestion(suggestion)">
            {{ suggestion }}
          </div>
        </div>
        <div
          v-if="inputValue && !suggestions.includes(inputValue)"
          class="text-xs p-2 cursor-pointer hover:bg-gray-100 text-blue-600 border-t"
          @click="createNewTag(inputValue)">
          Créer le tag "{{ inputValue }}"
        </div>
      </div>
    </div>

    <!-- Tags populaires - MASQUÉS en mode readonly -->
    <div v-if="!readonly && displayedCommonTags.length > 0" class="mt-2">
      <div class="text-xs text-gray-600 mb-1">Tags populaires:</div>
      <div class="flex flex-wrap gap-2">
        <button
          v-for="tag in displayedCommonTags"
          :key="tag.tag"
          class="px-2 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded-full transition-colors duration-200"
          :disabled="disabled"
          @click="selectTag(tag.tag)">
          {{ tag.tag }}
        </button>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted, watch } from 'vue';
import { XIcon } from 'lucide-vue-next';
import { useAssetsCacheStore } from '../../stores/assetsCache';
import { levenshteinDistance } from '../../utils/string';

export interface Tag {
  tag: string;
  count: number;
}

export default defineComponent({
  name: 'TagsPicker',
  components: {
    XIcon
  },
  props: {
    modelValue: {
      type: String,
      default: ''
    },
    placeholder: {
      type: String,
      default: ''
    },
    readonly: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    additionalSuggestions: {
      type: Array as () => string[],
      default: () => [
        'photographe', 'opérateur scanner 3d', 'opérateur rti', 'technicien imagerie scientifique',
        'responsable acquisition', 'documentaliste', 'archiviste', 'expert provenance données',
        'spécialiste', 'spécialiste métadonnées', 'gestionnaire données', 'ingénieur traitement image',
        'modélisateur 3d', 'opérateur fusion données', 'développeur réalité augmentée', 'annotateur',
        'analyste', 'développeur logiciel', 'coordinateur projet', 'responsable communication',
        'superviseur', 'reviewer', 'directeur de thèse', 'chercheur', 'technicien', 'opérateur',
        'responsable technique', 'gestionnaire projet', 'ingénieur système', 'responsable qualité',
        'évaluateur', 'manager', 'chef d\'équipe', 'formateur', 'rédacteur', 'auteur',
        'assistant recherche', 'assistant projet', 'administrateur base de données',
        'responsable sécurité', 'analyste données', 'consultant', 'facilitateur', 'planificateur',
        'chef de mission', 'opérateur drone', 'opérateur capteur climatique', 'ingénieur réseau',
        'spécialiste sig', 'géomètre', 'responsable conformité', 'expert domaine', 'analyste système',
        'opérateur photogrammétrie', 'responsable maintenance', 'opérateur lumière',
        'assistant documentaire', 'ingénieur logiciel', 'directeur laboratoire',
        'spécialiste multimodalité', 'chef développement', 'responsable innovation',
        'coordinateur scientifique', 'opérateur mesure', 'opérateur scanner laser',
        'gestionnaire inventaire'
      ]
    }
  },
  emits: ['update:modelValue'],
  setup(props, { emit }) {
    const assetsCacheStore = useAssetsCacheStore();
    
    const selectedTags = ref<string[]>([]);
    const inputValue = ref('');
    const showSuggestions = ref(false);
    const commonTags = ref<Tag[]>([]);
    const selectedIndex = ref(-1);
    const suggestions = ref<string[]>([]);

    // Convertir la chaîne de tags en tableau
    const initializeFromString = (value: string): void => {
      selectedTags.value = value
        .split(',')
        .map(tag => tag.trim())
        .filter(tag => tag.length > 0);
    };

    // Convertir le tableau en chaîne
    const updateModelValue = (): void => {
      if (props.readonly || props.disabled) return;
      emit('update:modelValue', selectedTags.value.join(', '));
    };

    // Charger les tags communs depuis le cache
    const loadCommonTags = async (): Promise<void> => {
      try {
        commonTags.value = await assetsCacheStore.getTags();
      } catch (error) {
        console.error('Erreur lors du chargement des tags:', error);
      }
    };

    // Tags communs filtrés (exclure ceux déjà sélectionnés)
    const displayedCommonTags = computed((): Tag[] => {
      const sortedTags = [...commonTags.value]
        .sort((a, b) => b.count - a.count);

      const availableTags = sortedTags
        .filter(tag => !selectedTags.value.includes(tag.tag));

      // Si nous avons moins de 5 tags disponibles parmi les plus communs,
      // on complète avec des suggestions additionnelles
      if (availableTags.length < 5) {
        const additionalNeeded = 5 - availableTags.length;
        const usedTags = new Set([...selectedTags.value, ...availableTags.map(t => t.tag)]);

        const extraSuggestions = props.additionalSuggestions
          .filter(tag => !usedTags.has(tag))
          .slice(0, additionalNeeded)
          .map(tag => ({ tag, count: 0 }));

        return [...availableTags, ...extraSuggestions];
      }

      return availableTags.slice(0, 5);
    });

    // Gérer les suggestions avec tolérance aux fautes de frappe
    const updateSuggestions = (): void => {
      if (!inputValue.value || props.readonly || props.disabled) {
        suggestions.value = [];
        return;
      }

      const allPossibleTags = [
        ...new Set([
          ...commonTags.value.map(t => t.tag),
          ...props.additionalSuggestions
        ])
      ];

      suggestions.value = allPossibleTags
        .filter(tag => {
          // Vérifier si le tag n'est pas déjà sélectionné
          if (selectedTags.value.includes(tag)) return false;

          // Correspondance exacte au début
          if (tag.toLowerCase().includes(inputValue.value.toLowerCase())) return true;

          // Tolérance aux fautes de frappe (distance de Levenshtein)
          const distance = levenshteinDistance(
            tag.toLowerCase().slice(0, inputValue.value.length),
            inputValue.value.toLowerCase()
          );
          return distance <= 2; // Tolérance de 2 caractères
        })
        .slice(0, 5);
    };

    // Gestionnaires d'événements
    const handleInput = (): void => {
      if (props.readonly || props.disabled) return;
      selectedIndex.value = -1;
      updateSuggestions();
    };

    const handleEnter = (): void => {
      if (props.readonly || props.disabled) return;
      if (selectedIndex.value >= 0 && suggestions.value[selectedIndex.value]) {
        selectSuggestion(suggestions.value[selectedIndex.value]);
      } else if (inputValue.value) {
        createNewTag(inputValue.value);
      }
    };

    const handleBackspace = (): void => {
      if (props.readonly || props.disabled) return;
      if (inputValue.value === '' && selectedTags.value.length > 0) {
        selectedTags.value.pop();
        updateModelValue();
      }
    };

    const handleArrowDown = (): void => {
      if (props.readonly || props.disabled) return;
      if (suggestions.value.length > 0) {
        selectedIndex.value = (selectedIndex.value + 1) % suggestions.value.length;
      }
    };

    const handleArrowUp = (): void => {
      if (props.readonly || props.disabled) return;
      if (suggestions.value.length > 0) {
        selectedIndex.value = selectedIndex.value <= 0
          ? suggestions.value.length - 1
          : selectedIndex.value - 1;
      }
    };

    const selectSuggestion = (tag: string): void => {
      if (props.readonly || props.disabled) return;
      if (!selectedTags.value.includes(tag)) {
        selectedTags.value.push(tag);
        updateModelValue();
      }
      inputValue.value = '';
      showSuggestions.value = false;
      suggestions.value = [];
    };

    const createNewTag = (tag: string): void => {
      if (props.readonly || props.disabled) return;
      const trimmedTag = tag.trim();
      if (trimmedTag && !selectedTags.value.includes(trimmedTag)) {
        selectedTags.value.push(trimmedTag);
        updateModelValue();
      }
      inputValue.value = '';
      showSuggestions.value = false;
    };

    const selectTag = (tag: string): void => {
      if (props.readonly || props.disabled) return;
      if (!selectedTags.value.includes(tag)) {
        selectedTags.value.push(tag);
        updateModelValue();
      }
    };

    const removeTag = (tag: string): void => {
      if (props.readonly || props.disabled) return;
      selectedTags.value = selectedTags.value.filter(t => t !== tag);
      updateModelValue();
    };

    // Initialisation et surveillance
    onMounted((): void => {
      loadCommonTags();
      if (props.modelValue) {
        initializeFromString(props.modelValue);
      }
    });

    watch(() => props.modelValue, (newValue: string): void => {
      if (newValue !== selectedTags.value.join(', ')) {
        initializeFromString(newValue);
      }
    });

    return {
      selectedTags,
      inputValue,
      showSuggestions,
      suggestions,
      commonTags,
      displayedCommonTags,
      selectedIndex,
      handleInput,
      handleEnter,
      handleBackspace,
      handleArrowDown,
      handleArrowUp,
      selectSuggestion,
      createNewTag,
      selectTag,
      removeTag
    };
  }
});
</script>

<style scoped>
.tags-picker {
  width: 100%;
}
</style>
